<script setup lang="ts">
import { computed } from "vue";
import { container } from "tsyringe";
import { useRoute } from "vue-router";
import { cva } from "class-variance-authority";

// shared
import { cn } from "@/shared/utils/cn";

// widgets
import { NavigationService } from "@/widgets/navigation/services/service/NavigationService";
import type {
    NavigationLinkProps,
    NavigationLinkEmits,
} from "@/widgets/navigation/models/interface";

const route = useRoute();
const navigationService = container.resolve(NavigationService);

const NavigationLinkTextVariant = cva("text-heading-medium font-bold font-pretendard", {
    variants: {
        selected: {
            true: "text-element-primary",
            false: "text-t-subtle",
        },
    },
});

const props = defineProps<NavigationLinkProps>();
const emits = defineEmits<NavigationLinkEmits>();

const isMatchedPath = computed(() => {
    const splitPath = route.path.split("/");

    return splitPath.some((path) => path === props.path.replace("/", ""));
});

/**
 * @description 현재 경로와 네비게이션 링크의 경로를 비교하여, 해당 경로에 따라 refetchTrigger 를 적용시킵니다.
 */
const onClickNavigationLink = () => {
    const refetchTrigger = navigationService.conditionallyApplyRefetchTrigger(route, props.path);
    emits("emit-refetch-trigger", refetchTrigger);
};
</script>

<template>
    <router-link :to="path" @click="onClickNavigationLink">
        <div
            class="flex justify-center items-center px-5 h-[5.6rem] rounded-[0.8rem] hover:bg-element-primary-lighter active:bg-element-primary-light"
        >
            <p :class="cn(NavigationLinkTextVariant({ selected: isMatchedPath }))">
                {{ text }}
            </p>
        </div>
    </router-link>
</template>
